import { GameSettings, GameState } from '../interfaces/game.interface';
import { RoomStatus } from '../interfaces/room.interface';
import { MatchEndResult } from '../interfaces/socket.interface';

// ============================================
// Client to Server - Room Management DTOs
// ============================================

/**
 * Create a new game room
 * This DTO should have validation on the backend
 * The host is always the sender's session playerId (never taken from the payload)
 */
export interface CreateRoomDto {
  roomName: string; // 3-20 characters
  password?: string; // Optional password for private rooms (min 4 characters)
  username?: string; // Display name for the host player
  settings?: Partial<GameSettings>; // Optional overrides; server fills in defaults
}

/**
 * Join a room using its joinCode (capability-based auth).
 * Used for public rooms (joinCode from list) and shared-link joins.
 * No password needed — possessing the joinCode IS the authorisation.
 */
export interface JoinRoomDto {
  joinCode: string;
}

/**
 * Verify password for a private room.
 * On success the server adds the player to the room and returns the joinCode.
 */
export interface VerifyRoomPasswordDto {
  roomId: string; // Public room id (always visible in the room list)
  password: string;
}

/**
 * Leave a room
 * The leaving player is always the sender's session playerId
 */
export interface LeaveRoomDto {
  roomId: string;
}

// ============================================
// Server to Client - Room State DTOs
// ============================================

/**
 * Room state update sent to clients.
 * `joinCode` is only populated for public rooms or when the player is already in the room.
 * Private rooms in the lobby list will NOT carry a joinCode.
 */
export interface RoomStateDto {
  id: string;
  joinCode?: string; // Conditionally included (see above)
  roomName: string;
  hostId: string;
  playerIds: string[];
  playerNames: Record<string, string>; // playerId -> display name
  /**
   * Colour index per member: the lowest one free when they joined, kept until
   * they leave. A leave never recolours anyone else.
   */
  playerColors: Record<string, number>;
  status: RoomStatus;
  hasPassword: boolean; // Indicates if room requires password to join
  createdAt: number;
  startedAt?: number;
  readyPlayers: Record<string, boolean>; // playerId -> isReady
  settings: GameSettings;
  disconnectedPlayers: string[]; // playerIds currently in the disconnect grace period
  /**
   * Members whose socket is live but whose page is not showing the room (on the
   * lobby, or not back on the room page yet). They hold their slot like a
   * disconnected member, and for as long.
   */
  awayPlayers: string[];
  /**
   * Who played the last match. Present only while the room is FINISHED: then
   * only these players may (re)join, e.g. someone who left the results screen.
   */
  matchRoster?: string[];
}

/**
 * Machine-readable reason carried by every `RoomServerEvents.ERROR` payload.
 * The client branches on this — never on the human-readable `message`.
 */
export enum RoomErrorCode {
  ROOM_NOT_FOUND = 'ROOM_NOT_FOUND',
  ROOM_FULL = 'ROOM_FULL',
  ROOM_NOT_WAITING = 'ROOM_NOT_WAITING',
  WRONG_PASSWORD = 'WRONG_PASSWORD',
  NOT_IN_ROOM = 'NOT_IN_ROOM',
  NOT_HOST = 'NOT_HOST',
  INVALID_STATE = 'INVALID_STATE',
  INSUFFICIENT_PLAYERS = 'INSUFFICIENT_PLAYERS',
  NOT_ALL_READY = 'NOT_ALL_READY',
  PLAYER_DISCONNECTED = 'PLAYER_DISCONNECTED',
  INVALID_INPUT = 'INVALID_INPUT',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Payload of `RoomServerEvents.ERROR`.
 */
export interface RoomErrorDto {
  type: RoomErrorCode;
  message: string;
}

/**
 * List of available rooms
 */
export interface RoomListDto {
  rooms: RoomStateDto[];
  total: number;
}

/**
 * Player joined room notification
 */
export interface PlayerJoinedRoomDto {
  roomId: string;
  playerId: string;
  playerName: string;
}

/**
 * Player left room notification
 */
export interface PlayerLeftRoomDto {
  roomId: string;
  playerId: string;
}

/**
 * Payload for playerJoined / playerLeft socket broadcasts
 */
export interface PlayerRoomEventDto {
  playerId: string;
  room: RoomStateDto;
}

/**
 * Payload for the matchStart event sent to all players.
 */
export interface MatchStartDto {
  gameDuration: number;
  playerOrders: Record<string, string[]>;
}

/**
 * Sent only to a member who (re)joins a room they already belong to — after a
 * reload, a dropped connection or a takeover. Carries everything needed to
 * rebuild the room view.
 */
export interface RoomResumedDto {
  room: RoomStateDto;
  gameState?: GameState; // present only if the room is IN_PROGRESS
  countdownStartsAt?: number; // present only if the room is STARTING
  matchResult?: MatchEndResult; // present only if the room is FINISHED
  rematchRequesters?: string[]; // present only if the room is FINISHED
}
